import React, { useState } from "react";
import * as XLSX from "xlsx";
import { Upload, Typography } from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import { SbButton, SbNotification, SbTable, SbTag } from "uiCore";
import useHistoricalEmployeeDataStore from "Store/useHistoricalEmployeeDataStore";

const { Text } = Typography;

// Same identity/org fields as the Employee Data onboarding tab, plus the
// two fields this upload exists for — Role Start Date and Role End Date.
// Multiple rows per employee are expected and allowed (one row per role
// they've historically held), unlike Employee Data's one-row-per-employee
// shape.
const EXPECTED_COLUMNS = [
  "Emp Id", "First Name", "Last Name", "Job Code", "Job Title",
  "Business Unit", "Legal Entity", "Function", "Sub-Function", "Level / Grade",
  "Role Start Date", "Role End Date",
];

const REQUIRED_COLUMNS = ["Emp Id", "Job Title", "Role Start Date"];

const parseWorkbook = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "binary", cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });

/**
 * Historical Employee Data upload — a frontend-only placeholder (no new
 * backend/BigQuery table this round; see the Talent Mobility algorithm
 * design doc's Engine A tenure-estimation section). Reuses the same
 * upload-then-preview shape as Employee Data onboarding, but this dataset's
 * whole purpose is different: it's not resolved against the Jobs Library at
 * all, it's a straight capture of "who held what role, from when to when,"
 * later used to ground Talent Mobility's tenure model in real data instead
 * of an LLM estimate. Multiple rows per employee are expected.
 */
const HistoricalEmployeeDataUpload = () => {
  const records = useHistoricalEmployeeDataStore((state) => state.records);
  const setRecords = useHistoricalEmployeeDataStore((state) => state.setRecords);
  const clearRecords = useHistoricalEmployeeDataStore((state) => state.clearRecords);
  const [previewRows, setPreviewRows] = useState(null);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState(null);

  const handleFileSelect = (file) => {
    setParseError(null);
    parseWorkbook(file)
      .then((rows) => {
        const missing = REQUIRED_COLUMNS.filter((col) => !(col in (rows[0] || {})));
        if (rows.length === 0) {
          setParseError("The file has no data rows.");
          return;
        }
        if (missing.length) {
          setParseError(`Missing required column(s): ${missing.join(", ")}.`);
          return;
        }
        setFileName(file.name);
        setPreviewRows(rows);
      })
      .catch(() => setParseError("Could not read this file — please upload a .xls, .xlsx, or .csv file."));
    return false; // prevent antd's default upload behavior — parsed client-side only
  };

  const handleConfirm = () => {
    setRecords(previewRows);
    SbNotification.success(
      "Historical data saved",
      `${previewRows.length} historical role record(s) from "${fileName}" saved. This data will ground Talent Mobility's tenure estimates once wired up.`
    );
    setPreviewRows(null);
    setFileName("");
  };

  const previewColumns = previewRows
    ? Object.keys(previewRows[0]).map((key) => ({ title: key, dataIndex: key, key, ellipsis: true }))
    : [];

  const savedColumns = records.length
    ? Object.keys(records[0]).map((key) => ({ title: key, dataIndex: key, key, ellipsis: true }))
    : [];

  return (
    <div style={{ padding: "16px 4px" }}>
      <Text type="secondary" style={{ display: "block", marginBottom: 16, maxWidth: 720 }}>
        Upload historical role data exported from your HRIS — the same identity/org fields as Employee
        Data, plus <strong>Role Start Date</strong> and <strong>Role End Date</strong>. Multiple rows per
        employee are expected (one row per role they've historically held). This is a placeholder upload
        for now — it captures the data so Talent Mobility's career-path tenure estimates can later be
        grounded in real history instead of an AI estimate, once a live pipeline exists.
      </Text>

      <Text style={{ display: "block", marginBottom: 16, fontSize: 12.5 }}>
        Expected columns: {EXPECTED_COLUMNS.map((c) => (
          <SbTag key={c} color={REQUIRED_COLUMNS.includes(c) ? "processing" : "default"} style={{ marginBottom: 4 }}>
            {c}
          </SbTag>
        ))}
      </Text>

      {!previewRows && (
        <Upload.Dragger
          accept=".xls,.xlsx,.csv"
          maxCount={1}
          showUploadList={false}
          multiple={false}
          beforeUpload={handleFileSelect}
          style={{ maxWidth: 560 }}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Upload historical employee role data</p>
          <p className="ant-upload-hint">Click to select or drag and drop your file here (.xls, .xlsx, or .csv)</p>
        </Upload.Dragger>
      )}

      {parseError && (
        <Text type="danger" style={{ display: "block", marginTop: 12 }}>{parseError}</Text>
      )}

      {previewRows && (
        <div style={{ marginTop: 16 }}>
          <Text strong>Preview — {fileName} ({previewRows.length} row(s))</Text>
          <SbTable
            columns={previewColumns}
            dataSource={previewRows.slice(0, 50)}
            rowKey={(_, i) => i}
            pagination={false}
            size="small"
            scroll={{ x: "max-content", y: 320 }}
            style={{ marginTop: 8, marginBottom: 12 }}
          />
          {previewRows.length > 50 && (
            <Text type="secondary" style={{ fontSize: 12 }}>Showing first 50 of {previewRows.length} rows.</Text>
          )}
          <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
            <SbButton buttonType="secondary" onClick={() => { setPreviewRows(null); setFileName(""); }}>
              Cancel
            </SbButton>
            <SbButton buttonType="primary" icon={<UploadOutlined />} onClick={handleConfirm}>
              Confirm &amp; Save
            </SbButton>
          </div>
        </div>
      )}

      {!previewRows && records.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <Text strong>Saved historical records ({records.length})</Text>
            <SbButton buttonType="secondary" size="small" onClick={clearRecords}>Clear all</SbButton>
          </div>
          <SbTable
            columns={savedColumns}
            dataSource={records.slice(0, 50)}
            rowKey={(_, i) => i}
            pagination={false}
            size="small"
            scroll={{ x: "max-content", y: 320 }}
          />
          {records.length > 50 && (
            <Text type="secondary" style={{ fontSize: 12 }}>Showing first 50 of {records.length} saved rows.</Text>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoricalEmployeeDataUpload;
